import { useEffect, useRef, useState } from "react";
import { isApiError } from "@bw/shared/api";

import CloseRounded from "@mui/icons-material/CloseRounded";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  createCourtsBulk,
  validateBulkCourts,
  type Court,
  type CourtBulkValidationResponse,
  type CourtCreate,
} from "@/libs/facilityApi";

import {
  generateCourtRange,
  normalizeCourtTags,
  parseCourtCsv,
  validateCourtDraft,
  type CourtDraftIssue,
} from "./bulk-court-draft";
import {
  BulkCourtAuthoring,
  BulkCourtReview,
  type BulkCourtAuthoringMode,
} from "./bulk-court-panels";

type DialogStep = "edit" | "review";
type ServerIssue = NonNullable<CourtBulkValidationResponse["issues"]>[number];

interface BulkCourtDialogProps {
  open: boolean;
  facilityId: string;
  onOpenChange: (open: boolean) => void;
  onCommitted: (courts: Court[]) => void | Promise<void>;
}

const DEFAULT_CSV = "name,capacity,tags";

function readConflictIssues(error: unknown): ServerIssue[] | null {
  if (
    !isApiError(error) ||
    error.status !== 409 ||
    error.code !== "COURT_INVENTORY_CONFLICT"
  ) {
    return null;
  }
  const issues = error.details?.issues;
  if (!Array.isArray(issues)) return [];
  return issues.flatMap((issue) => {
    if (!issue || typeof issue !== "object") return [];
    const candidate = issue as Record<string, unknown>;
    if (
      !Number.isInteger(candidate.row_number) ||
      candidate.field !== "name" ||
      (candidate.code !== "duplicate_in_draft" &&
        candidate.code !== "duplicate_existing") ||
      typeof candidate.message !== "string"
    ) {
      return [];
    }
    return [candidate as unknown as ServerIssue];
  });
}

function courtCountLabel(count: number): string {
  return `${count} ${count === 1 ? "court" : "courts"}`;
}

export function BulkCourtDialog({
  open,
  facilityId,
  onOpenChange,
  onCommitted,
}: BulkCourtDialogProps) {
  const [mode, setMode] = useState<BulkCourtAuthoringMode>("range");
  const [step, setStep] = useState<DialogStep>("edit");
  const [prefix, setPrefix] = useState("Court ");
  const [startNumber, setStartNumber] = useState("1");
  const [count, setCount] = useState("8");
  const [zeroPadding, setZeroPadding] = useState("0");
  const [capacity, setCapacity] = useState("");
  const [defaultTags, setDefaultTags] = useState("");
  const [csv, setCsv] = useState(DEFAULT_CSV);
  const [draft, setDraft] = useState<CourtCreate[] | null>(null);
  const [reviewedCourts, setReviewedCourts] = useState<CourtCreate[]>([]);
  const [clientIssues, setClientIssues] = useState<CourtDraftIssue[]>([]);
  const [serverIssues, setServerIssues] = useState<ServerIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const commitInFlight = useRef(false);
  const currentFacilityId = useRef(facilityId);
  const validationGeneration = useRef(0);
  const commitGeneration = useRef(0);
  currentFacilityId.current = facilityId;

  useEffect(() => {
    validationGeneration.current += 1;
    commitGeneration.current += 1;
    commitInFlight.current = false;
    if (!open) return;
    setMode("range");
    setStep("edit");
    setPrefix("Court ");
    setStartNumber("1");
    setCount("8");
    setZeroPadding("0");
    setCapacity("");
    setDefaultTags("");
    setCsv(DEFAULT_CSV);
    setDraft(null);
    setReviewedCourts([]);
    setClientIssues([]);
    setServerIssues([]);
    setError(null);
    setValidating(false);
    setCommitting(false);
  }, [facilityId, open]);

  const markSourceChanged = () => {
    setDraft(null);
    setClientIssues([]);
    setServerIssues([]);
    setError(null);
    setStep("edit");
  };

  const buildDraft = (): CourtCreate[] | null => {
    setClientIssues([]);
    setServerIssues([]);
    setError(null);
    if (mode === "csv") {
      const parsed = parseCourtCsv(csv);
      setClientIssues(parsed.issues);
      if (parsed.issues.length > 0) return null;
      setDraft(parsed.courts);
      return parsed.courts;
    }
    try {
      if (
        startNumber.trim() === "" ||
        count.trim() === "" ||
        zeroPadding.trim() === ""
      ) {
        throw new Error("Starting number, count, and padding are required.");
      }
      const generated = generateCourtRange({
        prefix,
        startNumber: Number(startNumber),
        count: Number(count),
        zeroPadding: Number(zeroPadding),
        maxPlayers: capacity.trim() === "" ? undefined : Number(capacity),
        tags: normalizeCourtTags(defaultTags.split(/[,|]/)),
      });
      setDraft(generated);
      return generated;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "The court draft is invalid.",
      );
      return null;
    }
  };

  const prepareDraft = () => {
    buildDraft();
  };

  const reviewDraft = async () => {
    if (validating || committing) return;
    const currentDraft = draft ?? buildDraft();
    if (!currentDraft) return;
    const localIssues = validateCourtDraft(currentDraft);
    setClientIssues(localIssues);
    if (localIssues.length > 0) return;

    setValidating(true);
    setError(null);
    setServerIssues([]);
    const requestedFacilityId = facilityId;
    const requestGeneration = ++validationGeneration.current;
    try {
      const validation = await validateBulkCourts(requestedFacilityId, {
        courts: currentDraft,
      });
      if (
        currentFacilityId.current !== requestedFacilityId ||
        validationGeneration.current !== requestGeneration
      ) {
        return;
      }
      if (!validation.valid) {
        setServerIssues(validation.issues ?? []);
        return;
      }
      // The server owns canonical normalization and natural ordering. Keep the
      // editable rows in that same order so a later commit-time conflict row
      // still identifies the court the operator sees at that row number.
      setDraft(validation.normalized_courts);
      setReviewedCourts(validation.normalized_courts);
      setStep("review");
    } catch (cause) {
      if (
        currentFacilityId.current !== requestedFacilityId ||
        validationGeneration.current !== requestGeneration
      ) {
        return;
      }
      setError(
        cause instanceof Error
          ? cause.message
          : "Court validation is temporarily unavailable.",
      );
    } finally {
      if (
        currentFacilityId.current === requestedFacilityId &&
        validationGeneration.current === requestGeneration
      ) {
        setValidating(false);
      }
    }
  };

  const commit = async () => {
    if (commitInFlight.current || reviewedCourts.length === 0) return;
    commitInFlight.current = true;
    setCommitting(true);
    setError(null);
    const requestedFacilityId = facilityId;
    const requestGeneration = ++commitGeneration.current;
    try {
      const result = await createCourtsBulk(requestedFacilityId, {
        courts: reviewedCourts,
      });
      if (
        currentFacilityId.current !== requestedFacilityId ||
        commitGeneration.current !== requestGeneration
      ) {
        return;
      }
      onOpenChange(false);
      try {
        await onCommitted(result.courts);
      } catch {
        // The atomic write already succeeded. Closing prevents a duplicate
        // retry; the inventory will reconcile on the next normal refresh.
      }
    } catch (cause) {
      if (
        currentFacilityId.current !== requestedFacilityId ||
        commitGeneration.current !== requestGeneration
      ) {
        return;
      }
      const conflictIssues = readConflictIssues(cause);
      if (conflictIssues !== null) {
        setServerIssues(conflictIssues);
        setStep("edit");
        setError(
          conflictIssues.length === 0
            ? "Court inventory changed. Review and validate the draft again."
            : null,
        );
      } else {
        setError(
          cause instanceof Error
            ? cause.message
            : "The courts could not be created.",
        );
      }
    } finally {
      if (
        currentFacilityId.current === requestedFacilityId &&
        commitGeneration.current === requestGeneration
      ) {
        commitInFlight.current = false;
        setCommitting(false);
      }
    }
  };

  const updateDraft = (index: number, update: Partial<CourtCreate>) => {
    setDraft(
      (current) =>
        current?.map((court, rowIndex) =>
          rowIndex === index ? { ...court, ...update } : court,
        ) ?? null,
    );
    setClientIssues([]);
    setServerIssues([]);
    setError(null);
  };

  const removeDraftRow = (index: number) => {
    setDraft(
      (current) => current?.filter((_, rowIndex) => rowIndex !== index) ?? null,
    );
    setClientIssues([]);
    setServerIssues([]);
  };

  const busy = validating || committing;
  const issueRows = [
    ...clientIssues.map((issue) => ({
      rowNumber: issue.rowNumber,
      message: issue.message,
    })),
    ...serverIssues.map((issue) => ({
      rowNumber: issue.row_number,
      message: issue.message,
    })),
  ];

  return (
    <Dialog
      aria-describedby="bulk-court-dialog-description"
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {
        if (!busy) onOpenChange(false);
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: "calc(100dvh - 32px)",
            width: "calc(100% - 32px)",
          },
        },
      }}
    >
      <DialogTitle sx={{ pr: 7, pb: 0.5 }}>
        {step === "review"
          ? `Review ${courtCountLabel(reviewedCourts.length)}`
          : "Add courts in bulk"}
      </DialogTitle>
      {!busy ? (
        <IconButton
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseRounded />
        </IconButton>
      ) : null}
      <DialogContentText
        id="bulk-court-dialog-description"
        sx={{ px: 3, pb: 2 }}
      >
        {step === "review"
          ? "Confirm every court below. The final save is atomic."
          : "Generate a numbered range or import CSV, edit the draft, then validate it against current inventory."}
      </DialogContentText>

      <DialogContent dividers sx={{ minHeight: 0, overflowY: "auto" }}>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {issueRows.length > 0 ? (
            <Alert severity="error">
              <Stack spacing={0.5}>
                {issueRows.map((issue, index) => (
                  <Typography
                    key={`${issue.rowNumber}-${issue.message}-${index}`}
                    variant="body2"
                  >
                    Row {issue.rowNumber}: {issue.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : null}

          {step === "review" ? (
            <BulkCourtReview courts={reviewedCourts} />
          ) : (
            <BulkCourtAuthoring
              mode={mode}
              busy={busy}
              prefix={prefix}
              startNumber={startNumber}
              count={count}
              zeroPadding={zeroPadding}
              capacity={capacity}
              defaultTags={defaultTags}
              csv={csv}
              draft={draft}
              onModeChange={(nextMode) => {
                setMode(nextMode);
                markSourceChanged();
              }}
              onPrefixChange={(value) => {
                setPrefix(value);
                markSourceChanged();
              }}
              onStartNumberChange={(value) => {
                setStartNumber(value);
                markSourceChanged();
              }}
              onCountChange={(value) => {
                setCount(value);
                markSourceChanged();
              }}
              onZeroPaddingChange={(value) => {
                setZeroPadding(value);
                markSourceChanged();
              }}
              onCapacityChange={(value) => {
                setCapacity(value);
                markSourceChanged();
              }}
              onDefaultTagsChange={(value) => {
                setDefaultTags(value);
                markSourceChanged();
              }}
              onCsvChange={(value) => {
                setCsv(value);
                markSourceChanged();
              }}
              onPrepare={prepareDraft}
              onUpdateDraft={updateDraft}
              onRemoveDraftRow={removeDraftRow}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === "review" ? (
          <>
            <Button
              disabled={committing}
              onClick={() => setStep("edit")}
              size="small"
              type="button"
              variant="outlined"
            >
              Back to edit
            </Button>
            <Button
              disabled={committing}
              onClick={commit}
              size="small"
              type="button"
              variant="contained"
            >
              {committing
                ? "Creating courts…"
                : `Create ${courtCountLabel(reviewedCourts.length)}`}
            </Button>
          </>
        ) : (
          <>
            <Button
              disabled={busy}
              onClick={() => onOpenChange(false)}
              size="small"
              type="button"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={reviewDraft}
              size="small"
              type="button"
              variant="contained"
            >
              {validating ? "Validating…" : "Review courts"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
